import { CustomTooltip } from '@fountain/fountain-ui-components';
import { Grid, TableCell } from '@material-ui/core';
import { GlobeAltOutlineIcon, Icon } from '@fountain/ripple/icon';
import React, { FC, memo } from 'react';

import { TYPE_COLUMN_WIDTH } from 'containers/ApplicantTableV2/constants';
import { SourceType } from 'containers/ApplicantTableV2/types';
import BoostLogo from 'images/sourcing/boost-blue.svg';
import IndeedLogo from 'images/sourcing/indeed-logo.svg';
import SpringLogo from 'images/sourcing/spring.svg';
import ZipRecruiterLogo from 'images/sourcing/ZipRecruiter_logo.svg';

import { logUnreachable } from 'utils/logUnreachable';

import useStyles from './styles';

export interface ApplicantTableV2TypeCellProps {
  sourceType: SourceType;
}

export const ApplicantTableV2TypeCell: FC<ApplicantTableV2TypeCellProps> = ({
  sourceType,
}) => {
  const classes = useStyles();

  const typeMap = (sourceType: SourceType) => {
    switch (sourceType) {
      case 'advanced': {
        return <img src={BoostLogo} alt="boost" className={classes.logo} />;
      }

      case 'campaign': {
        return <img src={IndeedLogo} alt="indeed" className={classes.logo} />;
      }

      case 'simple': {
        return <img src={BoostLogo} alt="boost" className={classes.logo} />;
      }

      case 'vonq': {
        return (
          <img
            src={SpringLogo}
            alt="fountain spring"
            className={classes.logo}
          />
        );
      }

      case 'ziprecruiter': {
        return (
          <img
            src={ZipRecruiterLogo}
            alt="ziprecruiter"
            className={classes.logo}
          />
        );
      }

      // `lite` is the sourcing enum's catch-all mode (non-integrated /
      // custom channels). There is no branded provider logo, so show a
      // neutral globe with an "Other source" label — this keeps a lite
      // applicant visually distinct from an organic (no-source) one, which
      // renders as an empty cell.
      case 'lite': {
        const label = `Other source: ${sourceType}`;
        return (
          <CustomTooltip title={label} dense>
            <Icon
              icon={GlobeAltOutlineIcon}
              size="md"
              color="neutral-400"
              role="img"
              aria-label={label}
              className={classes.liteIcon}
            />
          </CustomTooltip>
        );
      }

      case null:
      case undefined: {
        return null;
      }

      default: {
        // `sourceType` arrives over an untyped API boundary from the hire
        // monolith. An unrecognized value (e.g. a future sourcing mode)
        // must never crash the applicant table, so degrade to no icon
        // instead of throwing.
        return logUnreachable(sourceType, 'ApplicantTableV2TypeCell');
      }
    }
  };

  return (
    <TableCell
      role="cell"
      style={{
        minWidth: TYPE_COLUMN_WIDTH,
        maxWidth: TYPE_COLUMN_WIDTH,
      }}
      className={classes.typeCell}
    >
      <Grid container className={classes.sourceTypeCell}>
        <Grid item>{typeMap(sourceType)}</Grid>
      </Grid>
    </TableCell>
  );
};

export default memo(ApplicantTableV2TypeCell);
